import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Send, Users, MapPin, ArrowLeft, Flame, Sparkles } from "lucide-react";
import { PROVINCIAS_AR } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { nombre: "", email: "", whatsapp: "", provincia: "", edad: "", mensaje: "", quiere_voluntariar: false };

export const JoinForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [stats, setStats] = useState({ total: null, por_provincia: {} });

  const loadStats = () =>
    axios
      .get(`${API}/supporters/by-province`)
      .then(({ data }) => setStats(data))
      .catch(() => {});

  useEffect(() => {
    loadStats();
  }, []);

  // Provincias ordenadas: las que tienen simpatizantes primero, luego alfabético
  const provinciasOrdenadas = useMemo(() => {
    const cnt = stats.por_provincia || {};
    return [...PROVINCIAS_AR].sort((a, b) => {
      const da = cnt[a] || 0;
      const db = cnt[b] || 0;
      if (db !== da) return db - da;
      return a.localeCompare(b, "es");
    });
  }, [stats]);

  // Top 3 provincias destacadas (las más activas)
  const topProvincias = useMemo(() => {
    return provinciasOrdenadas.filter((p) => (stats.por_provincia?.[p] || 0) > 0).slice(0, 3);
  }, [provinciasOrdenadas, stats]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.whatsapp || !form.provincia) {
      toast.error("Completá nombre, email, WhatsApp y provincia.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, edad: form.edad ? Number(form.edad) : null };
      await axios.post(`${API}/supporters`, payload);
      setDone(true);
      toast.success(`¡Gracias por sumarte! Sos parte del movimiento en ${form.provincia}.`);
      await loadStats();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error("Este email ya está registrado. ¡Gracias por tu apoyo!");
      } else {
        toast.error("No pudimos registrarte. Probá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectProvincia = (p) => {
    setForm((f) => ({ ...f, provincia: p }));
    setTimeout(() => {
      const el = document.getElementById("join-form-step2");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const goBackToStep1 = () => {
    setForm((f) => ({ ...f, provincia: "" }));
    setTimeout(() => {
      const el = document.getElementById("sumate");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const contadorProvincia = stats.por_provincia?.[form.provincia] || 0;

  return (
    <section id="sumate" className="relative section-dark py-24 md:py-32 overflow-hidden" data-testid="join-section">
      <div aria-hidden className="absolute -top-32 right-1/3 w-96 h-96 bg-[#F5B400]/10 blur-3xl rounded-full" />
      <div aria-hidden className="absolute inset-0 grid-lines opacity-20" />

      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-[#F5B400]" />
            <span className="font-headings text-xs tracking-[0.3em] uppercase text-[#F5B400]">
              Sumate al movimiento
            </span>
            <span className="h-px w-10 bg-[#F5B400]" />
          </div>
          <h2 className="font-headings font-bold uppercase tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Me quiero <span className="text-[#F5B400]">sumar</span>
          </h2>
          {stats.total !== null && (
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-white/60" data-testid="join-count">
              <Users size={16} className="text-[#F5B400]" />
              <span className="tabular-nums font-headings">
                <strong className="text-white">{stats.total.toLocaleString("es-AR")}</strong> personas ya se sumaron al movimiento
              </span>
            </div>
          )}
        </div>

        <AnimatePresence mode="wait">
          {done ? (
            <SuccessCard nombre={form.nombre} provincia={form.provincia} email={form.email} onReset={() => { setForm(initial); setDone(false); }} />
          ) : !form.provincia ? (
            <Step1Selector
              key="step1"
              provincias={provinciasOrdenadas}
              counts={stats.por_provincia}
              topProvincias={topProvincias}
              onSelect={selectProvincia}
            />
          ) : (
            <Step2Form
              key="step2"
              form={form}
              onChange={onChange}
              onSubmit={submit}
              onBack={goBackToStep1}
              loading={loading}
              contadorProvincia={contadorProvincia}
            />
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

/* ============== STEP 1: Selector de provincia ============== */
const Step1Selector = ({ provincias, counts, topProvincias, onSelect }) => (
  <motion.div
    key="step1"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.4 }}
    data-testid="join-step-1"
  >
    <p className="text-center text-lg text-white/75 max-w-2xl mx-auto leading-relaxed mb-10">
      Elegí tu provincia para sumarte al movimiento. Vas a ver cuánta gente ya forma parte
      y te avisamos cuando arranquemos actividades cerca tuyo.
    </p>

    {topProvincias.length > 0 && (
      <div className="mb-10">
        <div className="flex items-center justify-center gap-2 text-[#F5B400] mb-4">
          <Flame size={16} />
          <span className="font-headings uppercase tracking-[0.25em] text-[10px]">Provincias más activas</span>
          <Flame size={16} />
        </div>
        <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto">
          {topProvincias.map((p, i) => (
            <ProvinciaCard
              key={p}
              provincia={p}
              count={counts[p] || 0}
              onClick={() => onSelect(p)}
              destacada
              rank={i + 1}
            />
          ))}
        </div>
      </div>
    )}

    <div className="text-center mb-5">
      <span className="font-headings uppercase tracking-[0.25em] text-[10px] text-white/40">Todas las provincias</span>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="join-province-grid">
      {provincias.map((p) => (
        <ProvinciaCard
          key={p}
          provincia={p}
          count={counts[p] || 0}
          onClick={() => onSelect(p)}
        />
      ))}
    </div>
  </motion.div>
);

const ProvinciaCard = ({ provincia, count, onClick, destacada = false, rank }) => {
  const proximoNum = count + 1;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative text-left p-4 sm:p-5 border transition-all overflow-hidden ${
        destacada
          ? "border-[#F5B400] bg-[#F5B400]/10 hover:bg-[#F5B400]/20"
          : "border-white/15 bg-white/[0.03] hover:border-[#F5B400]/60 hover:bg-white/[0.06]"
      }`}
      data-testid={`join-province-card-${provincia.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {destacada && rank && (
        <span className="absolute top-2 right-2 inline-flex items-center justify-center w-6 h-6 bg-[#F5B400] text-[#0A192F] text-[10px] font-headings font-bold rounded-full">
          {rank}
        </span>
      )}
      <div className="flex items-start gap-2 mb-2">
        <MapPin size={14} className={destacada ? "text-[#F5B400]" : "text-white/40 group-hover:text-[#F5B400] transition-colors"} />
        <span className="font-headings uppercase tracking-wide text-xs sm:text-sm text-white leading-tight flex-1">
          {provincia}
        </span>
      </div>
      <div className="text-[11px] uppercase tracking-wider text-white/50 font-headings tabular-nums">
        {count === 0 ? (
          <span className="text-[#F5B400]/80">Sé el primero</span>
        ) : (
          <>
            <strong className="text-white">{count.toLocaleString("es-AR")}</strong> sumado{count !== 1 && "s"}
            <span className="mx-1.5 text-white/30">·</span>
            <span className="text-[#F5B400]">sé el #{proximoNum.toLocaleString("es-AR")}</span>
          </>
        )}
      </div>
    </button>
  );
};

/* ============== STEP 2: Formulario contextual ============== */
const Step2Form = ({ form, onChange, onSubmit, onBack, loading, contadorProvincia }) => (
  <motion.div
    key="step2"
    id="join-form-step2"
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.4 }}
    data-testid="join-step-2"
  >
    <button
      type="button"
      onClick={onBack}
      className="mb-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#F5B400] font-headings transition-colors"
      data-testid="join-back-btn"
    >
      <ArrowLeft size={14} /> Cambiar provincia
    </button>

    <div className="border border-[#F5B400]/40 bg-[#F5B400]/10 px-5 sm:px-7 py-5 mb-6 flex items-center gap-4">
      <Sparkles className="text-[#F5B400] shrink-0" size={22} />
      <div className="flex-1">
        <div className="font-headings uppercase tracking-wide text-white text-base sm:text-lg leading-tight">
          Sumate al movimiento en <span className="text-[#F5B400]">{form.provincia}</span>
        </div>
        <div className="text-xs sm:text-sm text-white/70 mt-1">
          {contadorProvincia === 0 ? (
            <>Sé la primera persona en sumarse desde tu provincia.</>
          ) : (
            <>
              Ya somos <strong className="text-white tabular-nums">{contadorProvincia.toLocaleString("es-AR")}</strong> en {form.provincia}.
              Vas a ser el <strong className="text-[#F5B400] tabular-nums">#{(contadorProvincia + 1).toLocaleString("es-AR")}</strong>.
            </>
          )}
        </div>
      </div>
    </div>

    <form
      onSubmit={onSubmit}
      className="border border-white/15 bg-white/[0.03] p-6 sm:p-10 grid sm:grid-cols-2 gap-6"
      data-testid="join-form"
      noValidate
    >
      <Field label="Nombre completo *">
        <input
          className="input-line !text-white !border-white/30 focus:!border-[#F5B400]"
          name="nombre" value={form.nombre} onChange={onChange}
          placeholder="Tu nombre y apellido"
          data-testid="join-input-nombre" required
        />
      </Field>
      <Field label="Email *">
        <input
          type="email"
          className="input-line !text-white !border-white/30 focus:!border-[#F5B400]"
          name="email" value={form.email} onChange={onChange}
          placeholder="tucorreo@ejemplo.com"
          data-testid="join-input-email" required
        />
      </Field>
      <Field label="WhatsApp *">
        <input
          type="tel" inputMode="tel"
          className="input-line !text-white !border-white/30 focus:!border-[#F5B400]"
          name="whatsapp" value={form.whatsapp} onChange={onChange}
          placeholder="+54 11 1234 5678"
          data-testid="join-input-whatsapp" required
        />
      </Field>
      <Field label="Edad (opcional)">
        <input
          type="number" min="14" max="120"
          className="input-line !text-white !border-white/30 focus:!border-[#F5B400]"
          name="edad" value={form.edad} onChange={onChange}
          placeholder="25"
          data-testid="join-input-edad"
        />
      </Field>
      <Field label="Mensaje (opcional)" className="sm:col-span-2">
        <textarea
          name="mensaje" value={form.mensaje} onChange={onChange} rows={3}
          className="input-line !text-white !border-white/30 focus:!border-[#F5B400] resize-none"
          placeholder="Contanos por qué te interesa el movimiento o qué te gustaría aportar."
          data-testid="join-input-mensaje"
        />
      </Field>

      <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer group" data-testid="join-volunteer-label">
        <input
          type="checkbox"
          name="quiere_voluntariar"
          checked={form.quiere_voluntariar}
          onChange={onChange}
          className="mt-1 h-4 w-4 accent-[#F5B400]"
          data-testid="join-input-voluntariar"
        />
        <span className="text-sm text-white/75 group-hover:text-white transition-colors">
          Quiero participar como <strong className="text-[#F5B400]">Voluntario Digital</strong> en {form.provincia}{" "}
          (difusión por redes, contenidos, militancia online).
        </span>
      </label>

      <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-white/10">
        <p className="text-xs text-white/50 max-w-md">
          Tus datos no se comparten. Sólo los usamos para contactarte sobre el movimiento en tu provincia.
        </p>
        <button
          type="submit" disabled={loading}
          className="bg-[#F5B400] hover:bg-[#d99f00] disabled:opacity-60 text-[#0A192F] font-headings uppercase tracking-wide text-base px-8 py-4 inline-flex items-center gap-2 transition-all"
          data-testid="join-submit"
        >
          {loading ? (<><Loader2 className="animate-spin" size={18} /> Enviando…</>) : (<>Sumarme ahora <Send size={18} /></>)}
        </button>
      </div>
    </form>
  </motion.div>
);

/* ============== Success ============== */
const SuccessCard = ({ nombre, provincia, email, onReset }) => (
  <motion.div
    key="success"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0 }}
    className="border border-[#F5B400]/40 bg-[#F5B400]/5 p-10 sm:p-14 text-center max-w-3xl mx-auto"
    data-testid="join-success"
  >
    <CheckCircle2 className="mx-auto text-[#F5B400]" size={56} />
    <h3 className="font-headings text-3xl sm:text-4xl uppercase font-bold text-white mt-6">
      ¡Gracias, {nombre.split(" ")[0]}!
    </h3>
    <p className="mt-4 text-white/80 max-w-md mx-auto">
      Sos parte del movimiento en <strong className="text-[#F5B400]">{provincia}</strong>.
      Te vamos a escribir a <strong className="text-[#F5B400]">{email}</strong> cuando haya novedades.
    </p>
    <button
      onClick={onReset}
      className="mt-8 text-sm uppercase tracking-[0.2em] font-headings text-white/70 hover:text-[#F5B400] transition-colors"
      data-testid="join-reset"
    >
      Sumar a otra persona
    </button>
  </motion.div>
);

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="block text-xs uppercase tracking-[0.2em] text-white/60 mb-2 font-headings">
      {label}
    </label>
    {children}
  </div>
);

export default JoinForm;
